import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { useCurrentAdvisor } from './useCurrentAdvisor'

/**
 * Returns unread notification counts split by page:
 *   claimsCount  — notifications for Dispatch portal tasks (claim_id IS NOT NULL)
 *   tasksCount   — notifications for regular tasks (claim_id IS NULL)
 *
 * Uses two queries and subscribes to realtime so both badges stay live.
 */
export function useTaskNotificationCounts() {
  const { advisor } = useCurrentAdvisor()
  const [claimsCount, setClaimsCount] = useState(0)
  const [tasksCount, setTasksCount] = useState(0)

  const refresh = useCallback(async (advisorId) => {
    // 1. All unread notifications for this advisor that have a task_id
    const { data: notifs } = await supabase
      .from('sfp_notifications')
      .select('id, task_id')
      .eq('advisor_id', advisorId)
      .eq('is_read', false)

    const taskIds = [...new Set((notifs ?? []).map(n => n.task_id).filter(Boolean))]
    if (taskIds.length === 0) { setClaimsCount(0); setTasksCount(0); return }

    // 2. Which of those tasks are dispatch claims vs regular?
    const { data: tasks } = await supabase
      .from('sfp_tasks')
      .select('id, claim_id')
      .in('id', taskIds)
      .is('deleted_at', null)

    const claimTaskIds = new Set((tasks ?? []).filter(t => t.claim_id != null).map(t => t.id))

    let claims = 0
    let regular = 0
    taskIds.forEach(id => {
      if (claimTaskIds.has(id)) claims++
      else regular++
    })

    setClaimsCount(claims)
    setTasksCount(regular)
  }, [])

  useEffect(() => {
    if (!advisor?.id) return
    refresh(advisor.id)

    const ch = supabase
      .channel(`task-notif-counts-${advisor.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'sfp_notifications',
        filter: `advisor_id=eq.${advisor.id}`,
      }, () => refresh(advisor.id))
      .subscribe()

    return () => supabase.removeChannel(ch)
  }, [advisor?.id, refresh])

  return { claimsCount, tasksCount }
}
